import React, { useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { payrollAmountInputProps } from "./payrollAmountInput";
import { getPayrollComponentDeleteState } from "./payrollComponentDeletion";
import dayjs from "dayjs";

const effectByKind = {
  Earning: "Add",
  "Employee Deduction": "Subtract",
  "Employer Contribution": "Employer Only",
};

const PayrollComponentsTab = ({
  components,
  saving,
  saveComponent,
  deleteComponent,
  copyVersion,
}) => {
  const [form] = Form.useForm();
  const [copyForm] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [copySource, setCopySource] = useState(null);
  const calculationMethod = Form.useWatch("calculationMethod", form);
  const frequency = Form.useWatch("frequency", form);

  const showForm = (component = null) => {
    setSelected(component);
    form.resetFields();
    form.setFieldsValue(
      component
        ? {
            ...component,
            effectiveFrom: component.effectiveFrom
              ? dayjs(component.effectiveFrom)
              : null,
            effectiveTo: component.effectiveTo
              ? dayjs(component.effectiveTo)
              : null,
          }
        : {
            kind: "Earning",
            frequency: "Recurring Monthly",
            calculationMethod: "Fixed Amount",
            calculationBasis: "Basic Salary",
            taxTreatment: "Taxable",
            netPayEffect: "Add",
            effectiveFrom: dayjs(),
            calculationBands: [],
            scheduledMonths: [],
            prorateWithSalary: false,
            allowEmployeeOverride: true,
            isActive: true,
          },
    );
    setOpen(true);
  };

  const submit = async () => {
    const values = await form.validateFields();
    const result = await saveComponent(selected?.componentId, {
      ...values,
      effectiveFrom: values.effectiveFrom?.format("YYYY-MM-DD") || null,
      effectiveTo: values.effectiveTo?.format("YYYY-MM-DD") || null,
    });
    if (result) setOpen(false);
  };

  const openCopy = (component) => {
    setCopySource(component);
    copyForm.resetFields();
    copyForm.setFieldsValue({ effectiveFrom: dayjs().add(1, "day") });
  };

  const submitCopy = async () => {
    const values = await copyForm.validateFields();
    const result = await copyVersion(copySource.componentId, {
      effectiveFrom: values.effectiveFrom.format("YYYY-MM-DD"),
      effectiveTo: values.effectiveTo?.format("YYYY-MM-DD") || null,
    });
    if (result) setCopySource(null);
  };

  const columns = [
    {
      title: "Component",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <strong>{row.name}</strong>
          <span style={{ color: "#8c8c8c" }}>
            {row.code} · v{row.versionNumber}
          </span>
        </Space>
      ),
    },
    { title: "Type", dataIndex: "kind", render: (value) => <Tag>{value}</Tag> },
    {
      title: "Calculation",
      render: (_, row) => {
        if (row.calculationMethod === "Fixed Amount") {
          return `Fixed UGX ${Number(row.defaultAmount || 0).toLocaleString("en-UG")}`;
        }
        if (row.calculationMethod === "Percentage of Basic Salary") {
          return `${Number(row.defaultPercentage || 0)}% of basic salary`;
        }
        return row.calculationMethod;
      },
    },
    { title: "Tax treatment", dataIndex: "taxTreatment", responsive: ["md"] },
    {
      title: "Effective",
      responsive: ["lg"],
      render: (_, row) =>
        `${row.effectiveFrom ? dayjs(row.effectiveFrom).format("DD MMM YYYY") : "Any date"} — ${row.effectiveTo ? dayjs(row.effectiveTo).format("DD MMM YYYY") : "Open"}`,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (value) => (
        <Tag color={value ? "success" : "default"}>
          {value ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      width: 130,
      align: "center",
      render: (_, row) => {
        const deleteState = getPayrollComponentDeleteState(row, saving);
        const deleteButton = (
          <Tooltip title={deleteState.reason}>
            <span
              style={{ display: "inline-block" }}
              tabIndex={deleteState.disabled ? 0 : undefined}
              aria-label={deleteState.disabled ? deleteState.reason : undefined}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={deleteState.disabled}
                aria-label={deleteState.reason}
              />
            </span>
          </Tooltip>
        );

        return (
          <Space size="small">
            <Tooltip title="Edit">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => showForm(row)}
              />
            </Tooltip>
            <Tooltip title="Copy as a new version">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => openCopy(row)}
              />
            </Tooltip>
            {row.isActive ? (
              deleteButton
            ) : (
              <Popconfirm
                title={`Permanently delete ${row.name} version ${row.versionNumber}?`}
                description={`This removes this version from payroll setup. If any version of ${row.code} has been used, keep it inactive or end-dated instead.`}
                okText="Delete version"
                okButtonProps={{ danger: true }}
                onConfirm={() => deleteComponent(row.componentId)}
                disabled={deleteState.disabled}
              >
                {deleteButton}
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <div style={{ textAlign: "right" }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => showForm()}
        >
          Add component
        </Button>
      </div>
      <Table
        rowKey="componentId"
        columns={columns}
        dataSource={components}
        scroll={{ x: 760 }}
      />

      <Modal
        title={selected ? "Edit payroll component" : "Add payroll component"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={submit}
        confirmLoading={saving}
        width={720}
        okText="Save component"
      >
        <Form form={form} layout="vertical">
          {selected && (
            <Tag color="blue">
              Editing {selected.code} version {selected.versionNumber}
            </Tag>
          )}
          <Space align="start" wrap style={{ width: "100%", marginTop: 12 }}>
            <Form.Item name="name" label="Name" rules={[{ required: true }]}>
              <Input style={{ width: 300 }} />
            </Form.Item>
            <Form.Item name="code" label="Code" rules={[{ required: true }]}>
              <Input style={{ width: 180 }} />
            </Form.Item>
          </Space>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="effectiveFrom" label="Effective from">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="effectiveTo" label="Effective to">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Space align="start" wrap>
            <Form.Item
              name="kind"
              label="Component type"
              rules={[{ required: true }]}
            >
              <Select
                style={{ width: 220 }}
                options={Object.keys(effectByKind).map((value) => ({
                  value,
                  label: value,
                }))}
                onChange={(value) => {
                  form.setFieldValue("netPayEffect", effectByKind[value]);
                  form.setFieldValue(
                    "allowEmployeeOverride",
                    value !== "Employer Contribution",
                  );
                  if (value === "Employee Deduction") {
                    form.setFieldValue("taxTreatment", "Non-Taxable");
                  }
                }}
              />
            </Form.Item>
            <Form.Item
              name="frequency"
              label="Frequency"
              rules={[{ required: true }]}
            >
              <Select
                style={{ width: 190 }}
                options={[
                  "Recurring Monthly",
                  "One-Off",
                  "Manual Input",
                  "Scheduled Months",
                  "Date Range",
                ].map((value) => ({ value, label: value }))}
              />
            </Form.Item>
            <Form.Item name="netPayEffect" label="Net-pay effect">
              <Select
                disabled
                style={{ width: 160 }}
                options={Object.values(effectByKind).map((value) => ({
                  value,
                  label: value,
                }))}
              />
            </Form.Item>
          </Space>
          <Space align="start" wrap>
            <Form.Item
              name="calculationMethod"
              label="Calculation method"
              rules={[{ required: true }]}
            >
              <Select
                style={{ width: 250 }}
                options={[
                  { value: "Fixed Amount", label: "Fixed" },
                  {
                    value: "Percentage of Basis",
                    label: "Percentage of a basis",
                  },
                  { value: "Banded", label: "Salary range" },
                ]}
              />
            </Form.Item>
            {calculationMethod === "Fixed Amount" && (
              <Form.Item
                name="defaultAmount"
                label="Default amount (UGX)"
                rules={[{ required: true }]}
              >
                <InputNumber
                  {...payrollAmountInputProps}
                  min={0}
                  precision={2}
                  style={{ width: 200 }}
                />
              </Form.Item>
            )}
          </Space>
          {["Percentage of Basis", "Banded"].includes(calculationMethod) && (
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="calculationBasis"
                  label="Calculation basis"
                  rules={[{ required: true }]}
                >
                  <Select
                    options={[
                      "Basic Salary",
                      "Gross Earnings",
                      "Taxable Earnings",
                    ].map((value) => ({ value, label: value }))}
                  />
                </Form.Item>
              </Col>
              {calculationMethod === "Percentage of Basis" && (
                <Col xs={24} md={12}>
                  <Form.Item
                    name="defaultPercentage"
                    label="Default percentage"
                    rules={[{ required: true }]}
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      precision={4}
                      addonAfter="%"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          )}
          {calculationMethod === "Banded" && (
            <Form.List name="calculationBands">
              {(fields, { add, remove }) => (
                <Space direction="vertical" style={{ width: "100%" }}>
                  {fields.map((field, index) => (
                    <Row gutter={8} key={field.key}>
                      <Col span={6}>
                        <Form.Item
                          name={[field.name, "lowerBound"]}
                          label={index === 0 ? "From" : ""}
                          rules={[{ required: true }]}
                        >
                          <InputNumber
                            {...payrollAmountInputProps}
                            min={0}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          name={[field.name, "upperBound"]}
                          label={index === 0 ? "To" : ""}
                        >
                          <InputNumber
                            {...payrollAmountInputProps}
                            min={0}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          name={[field.name, "baseAmount"]}
                          label={index === 0 ? "Base amount" : ""}
                        >
                          <InputNumber
                            {...payrollAmountInputProps}
                            min={0}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item
                          name={[field.name, "rate"]}
                          label={index === 0 ? "Rate %" : ""}
                        >
                          <InputNumber
                            min={0}
                            max={100}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={1}>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(field.name)}
                        />
                      </Col>
                    </Row>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add({ baseAmount: 0, rate: 0 })}
                  >
                    Add calculation band
                  </Button>
                  <Row gutter={12}>
                    <Col xs={12} md={6}>
                      <Form.Item name="minimumAmount" label="Minimum amount">
                        <InputNumber
                          {...payrollAmountInputProps}
                          min={0}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Item name="maximumAmount" label="Maximum amount">
                        <InputNumber
                          {...payrollAmountInputProps}
                          min={0}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Space>
              )}
            </Form.List>
          )}
          {frequency === "Scheduled Months" && (
            <Form.Item
              name="scheduledMonths"
              label="Scheduled months"
              rules={[{ required: true, type: "array", min: 1 }]}
            >
              <Select
                mode="multiple"
                options={[
                  "January",
                  "February",
                  "March",
                  "April",
                  "May",
                  "June",
                  "July",
                  "August",
                  "September",
                  "October",
                  "November",
                  "December",
                ].map((label, index) => ({ value: index + 1, label }))}
              />
            </Form.Item>
          )}
          <Form.Item
            name="taxTreatment"
            label="Tax treatment"
            rules={[{ required: true }]}
          >
            <Select
              options={["Taxable", "Non-Taxable"].map((value) => ({
                value,
                label: value,
              }))}
            />
          </Form.Item>
          <Space size="large" wrap>
            <Form.Item
              name="allowEmployeeOverride"
              label="Show in employee earnings & deductions"
              valuePropName="checked"
              tooltip="Make this earning or deduction available for an employee-specific amount during setup."
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="prorateWithSalary"
              label="Prorate fixed amount with salary"
              valuePropName="checked"
              tooltip="Reduce this recurring fixed amount by the same fraction when an employee joins or leaves during the month."
            >
              <Switch />
            </Form.Item>
            <Form.Item name="isActive" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          copySource
            ? `Copy ${copySource.code} v${copySource.versionNumber}`
            : "Copy component version"
        }
        open={Boolean(copySource)}
        onCancel={() => setCopySource(null)}
        onOk={submitCopy}
        confirmLoading={saving}
        okText="Create new version"
      >
        <Form form={copyForm} layout="vertical">
          <Form.Item
            name="effectiveFrom"
            label="New version effective from"
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="effectiveTo" label="New version effective to">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default PayrollComponentsTab;
